import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { SUBJECT_OPTIONS } from './StudentFormDialog';

const B = { primary: '#4357AD', dark: '#1E2A4A' };

const fieldSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 3,
    '& fieldset': { borderColor: '#EDE9FE' },
    '&:hover fieldset': { borderColor: B.primary },
    '&.Mui-focused fieldset': { borderColor: B.primary },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: B.primary },
};

const schema = yup.object({
  name: yup.string().required("Ім'я обов'язкове"),
  email: yup.string().email('Невірний email').required('Email обовʼязковий'),
  password: yup.string().min(6, 'Мінімум 6 символів'),
});

const UserFormDialog = ({ open, onClose, onSubmit, initialValues, title, isEdit, showSubjects = false, showContactFields = false }) => {
  const [selectedSubjects, setSelectedSubjects] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialValues,
  });

  useEffect(() => {
    if (open) {
      reset({
        name: initialValues?.name || '',
        email: initialValues?.email || '',
        password: initialValues?.password || '',
        phone: initialValues?.phone || '',
        notes: initialValues?.notes || '',
      });
      const subs = initialValues?.subjects;
      if (subs && typeof subs === 'string') {
        setSelectedSubjects(subs.split(',').map((s) => s.trim()).filter(Boolean));
      } else if (Array.isArray(subs)) {
        setSelectedSubjects(subs);
      } else {
        setSelectedSubjects([]);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, reset]);

  const toggleSubject = (value) => {
    setSelectedSubjects((prev) =>
      prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]
    );
  };

  const handleFormSubmit = handleSubmit(async (values) => {
    const payload = { ...values };
    if (isEdit && payload.password === initialValues?.password) {
      delete payload.password;
    }
    if (showSubjects) {
      payload.subjects = selectedSubjects;
    }
    await onSubmit(payload);
    onClose();
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 4, boxShadow: '0 20px 60px rgba(67,87,173,0.18)', overflow: 'hidden' } }}
    >
      {/* Header */}
      <Box sx={{ background: `linear-gradient(135deg, ${B.dark} 0%, ${B.primary} 100%)`, px: 3, py: 2.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 40, height: 40, borderRadius: 2.5, bgcolor: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PersonIcon sx={{ color: '#fff', fontSize: 22 }} />
        </Box>
        <Typography variant="h6" fontWeight={700} sx={{ color: '#fff' }}>{title}</Typography>
      </Box>

      <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
        <Stack spacing={2.5}>
          <TextField label="Ім'я" {...register('name')} error={Boolean(errors.name)} helperText={errors.name?.message} fullWidth sx={fieldSx} />
          <TextField label="Email" type="email" {...register('email')} error={Boolean(errors.email)} helperText={errors.email?.message} fullWidth sx={fieldSx} />
          <TextField label="Пароль" {...register('password')} error={Boolean(errors.password)} helperText={errors.password?.message} fullWidth sx={fieldSx} />

          {showContactFields && (
            <>
              <TextField label="Телефон" {...register('phone')} fullWidth sx={fieldSx} placeholder="+380..." />
              <TextField label="Примітки" {...register('notes')} fullWidth multiline minRows={2} sx={fieldSx} />
            </>
          )}

          {showSubjects && (
            <Box>
              <Typography variant="caption" fontWeight={600} sx={{ color: '#8891A5', mb: 1, display: 'block' }}>
                Предмети
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                {SUBJECT_OPTIONS.map((option) => {
                  const selected = selectedSubjects.includes(option.value);
                  return (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onClick={() => toggleSubject(option.value)}
                      sx={{
                        fontWeight: 600,
                        fontSize: 12,
                        cursor: 'pointer',
                        border: selected ? `2px solid ${B.primary}` : '2px solid #EDE9FE',
                        bgcolor: selected ? '#EDE9FE' : '#fff',
                        color: selected ? B.primary : '#8891A5',
                        transition: 'all 0.15s',
                        '&:hover': { borderColor: B.primary, bgcolor: '#F0EDFF' },
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, gap: 1 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none', color: '#8891A5', fontWeight: 600, borderRadius: 3, px: 2.5 }}>
          Скасувати
        </Button>
        <Button
          variant="contained"
          onClick={handleFormSubmit}
          disabled={isSubmitting}
          sx={{ textTransform: 'none', bgcolor: B.primary, '&:hover': { bgcolor: '#3648A0' }, borderRadius: 3, fontWeight: 700, px: 3, boxShadow: '0 4px 14px rgba(67,87,173,0.3)' }}
        >
          Зберегти
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserFormDialog;
